/**
 * 로그 레벨 관리 유틸리티
 * 개발/프로덕션 환경에 따라 로그를 자동으로 필터링합니다.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const IS_PRODUCTION = import.meta.env.MODE === 'prod'
const IS_DEVELOPMENT = import.meta.env.MODE === 'dev' || import.meta.env.MODE === 'loc'

/**
 * 로그 레벨별 출력 여부 결정
 */
const shouldLog = (level: LogLevel): boolean => {
  if (IS_PRODUCTION) {
    // 프로덕션: warn, error만
    return level === 'warn' || level === 'error'
  }
  // 개발: 모두 출력
  return true
}

/**
 * 민감한 정보를 마스킹하는 함수
 */
export function maskSensitiveData(data: any): any {
  if (typeof data === 'string') {
    // JWT 토큰 마스킹: eyJhbGc... → eyJ***
    if (data.startsWith('eyJ') && data.length > 20) {
      return `${data.substring(0, 10)}***`
    }

    // 이메일 마스킹: user@example.com → u***@e***.com
    if (data.includes('@') && data.includes('.')) {
      const [local, domain] = data.split('@')
      if (local.length > 2 && domain.length > 4) {
        const maskedLocal = `${local[0]}***`
        const maskedDomain = `${domain[0]}***${domain.substring(domain.length - 4)}`
        return `${maskedLocal}@${maskedDomain}`
      }
    }

    return data
  }

  if (typeof data === 'object' && data !== null) {
    const masked: any = Array.isArray(data) ? [] : {}

    for (const key in data) {
      // 토큰 관련 키 완전 마스킹
      if (/token|password|secret|key/i.test(key)) {
        masked[key] = '***MASKED***'
      }
      // 이메일 마스킹
      else if (/email/i.test(key) && typeof data[key] === 'string') {
        masked[key] = maskSensitiveData(data[key])
      }
      // 재귀적으로 마스킹
      else if (typeof data[key] === 'object') {
        masked[key] = maskSensitiveData(data[key])
      } else {
        masked[key] = data[key]
      }
    }

    return masked
  }

  return data
}

/**
 * 중앙화된 로거
 */
export const logger = {
  /**
   * 디버그 로그 (개발 환경에서만)
   */
  debug: (message: string, ...args: any[]) => {
    if (shouldLog('debug')) {
      console.log(`🔍 [DEBUG] ${message}`, ...args)
    }
  },

  /**
   * 정보 로그 (개발 환경에서만)
   */
  info: (message: string, ...args: any[]) => {
    if (shouldLog('info')) {
      console.log(`ℹ️ [INFO] ${message}`, ...args)
    }
  },

  /**
   * 경고 로그 (모든 환경)
   */
  warn: (message: string, ...args: any[]) => {
    if (shouldLog('warn')) {
      console.warn(`⚠️ [WARN] ${message}`, ...args)
    }
  },

  /**
   * 에러 로그 (모든 환경, 자동 마스킹)
   */
  error: (message: string, ...args: any[]) => {
    if (shouldLog('error')) {
      const maskedArgs = args.map(arg => maskSensitiveData(arg))
      console.error(`❌ [ERROR] ${message}`, ...maskedArgs)
    }
  },

  /**
   * 인증 관련 로그 (자동 마스킹)
   */
  auth: (message: string, ...args: any[]) => {
    if (shouldLog('info')) {
      const maskedArgs = args.map(arg => maskSensitiveData(arg))
      console.log(`🔐 [AUTH] ${message}`, ...maskedArgs)
    }
  },

  /**
   * 토큰 관련 로그 (자동 마스킹)
   */
  token: (message: string, ...args: any[]) => {
    if (shouldLog('info')) {
      const maskedArgs = args.map(arg => maskSensitiveData(arg))
      console.log(`🎫 [TOKEN] ${message}`, ...maskedArgs)
    }
  },

  /**
   * API 관련 로그
   */
  api: (message: string, ...args: any[]) => {
    if (shouldLog('debug')) {
      console.log(`🌐 [API] ${message}`, ...args)
    }
  },

  /**
   * FCM 관련 로그
   */
  fcm: (message: string, ...args: any[]) => {
    if (shouldLog('info')) {
      console.log(`🔔 [FCM] ${message}`, ...args)
    }
  },

  /**
   * 스토리지 관련 로그
   */
  storage: (message: string, ...args: any[]) => {
    if (shouldLog('debug')) {
      console.log(`💾 [STORAGE] ${message}`, ...args)
    }
  },

  /**
   * 성공 로그
   */
  success: (message: string, ...args: any[]) => {
    if (shouldLog('info')) {
      console.log(`✅ [SUCCESS] ${message}`, ...args)
    }
  },

  /**
   * 그룹 로그 시작
   */
  group: (title: string) => {
    if (IS_DEVELOPMENT) {
      console.group(title)
    }
  },

  /**
   * 그룹 로그 종료
   */
  groupEnd: () => {
    if (IS_DEVELOPMENT) {
      console.groupEnd()
    }
  }
}

/**
 * 환경 정보 로깅
 */
export const logEnvironment = () => {
  if (IS_DEVELOPMENT) {
    console.log('🌍 Environment:', {
      mode: import.meta.env.MODE,
      isDev: IS_DEVELOPMENT,
      isProd: IS_PRODUCTION,
      baseURL: import.meta.env.VITE_API_BASE_URL
    })
  }
}
